import { COACHING_MANAGER_NOTE_MAX_LENGTH } from "@repo/app-types/entities/teammatePlaybookCoachingManagerInput";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Textarea } from "@repo/ui/Inputs/textarea";
import {
  SimpleTreeSelect,
  type SimpleTreeSelectOption,
} from "@repo/ui/Select/simpleTreeSelect";
import { IconFlagCheck, IconPencil } from "@tabler/icons-react";
import { useEffect, useState, type FC, type ReactNode } from "react";
import { ManagerNoteSurface } from "./managerNoteSurface.js";

/** Show the character counter once the note is at 80% of the limit. */
const NOTE_COUNTER_THRESHOLD = COACHING_MANAGER_NOTE_MAX_LENGTH * 0.8;

export interface NextWeekGoalCardShellProps {
  title: string;
  /** Supporting text beneath the title. */
  subtitle?: string;
  /** Right-aligned header controls (the period selector); hidden while editing. */
  headerControls?: ReactNode;
  children: ReactNode;
}

/** The card chrome for the next-week goal view, matching the weekly goal card. */
export const NextWeekGoalCardShell: FC<NextWeekGoalCardShellProps> = (
  props,
) => {
  const { title, subtitle, headerControls, children } = props;

  return (
    <section className="relative flex flex-col gap-y-4 rounded-lg border border-border-light bg-card p-4 shadow-xs">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            <IconFlagCheck className="size-4 shrink-0 text-emerald-700" />
            <h2 className="text-sm leading-5 font-semibold text-foreground">
              {title}
            </h2>
          </div>
          {subtitle ? (
            <p className="mt-0.5 pl-6 text-xs text-muted-foreground">
              {subtitle}
            </p>
          ) : null}
        </div>

        {headerControls}
      </header>

      {children}
    </section>
  );
};

export interface NextWeekGoalEmptyStateProps {
  teammateFirstName: string;
  onSetGoal: () => void;
}

/** Shown on the next-week view when no manager goal has been set yet. */
export const NextWeekGoalEmptyState: FC<NextWeekGoalEmptyStateProps> = (
  props,
) => {
  const { teammateFirstName, onSetGoal } = props;

  return (
    <div className="flex min-h-64 flex-col items-center justify-center rounded-lg border border-dashed border-border-light px-6 py-10 text-center">
      <IconFlagCheck className="size-6 text-muted-foreground" />
      <p className="mt-4 max-w-2xl text-sm leading-6 font-medium text-foreground">
        Alpharun will choose next week&apos;s goal automatically.
      </p>
      <p className="mt-1 max-w-2xl text-sm leading-6 text-muted-foreground">
        You can choose the criteria for {teammateFirstName} instead.
      </p>
      <Button
        variant={ButtonVariant.Primary}
        size={Size.MEDIUM}
        onClick={onSetGoal}
        className="mt-4"
      >
        Choose next week&apos;s goal
      </Button>
    </div>
  );
};

export interface NextWeekGoalSavedProps {
  criterionName: string;
  /** The criterion info popover trigger, when grading context is available. */
  criterionInfoControl?: ReactNode;
  note: string | null;
  /** Opens the goal editor, where the note is edited with the goal. */
  onEditNote: () => void;
}

/** The saved manager-set goal, shown until the week's coaching is generated. */
export const NextWeekGoalSaved: FC<NextWeekGoalSavedProps> = (props) => {
  const { criterionName, criterionInfoControl, note, onEditNote } = props;

  return (
    <div className="group space-y-4 rounded-lg border border-border-light bg-white p-4">
      {/* Styled to match the criterion header above the score trend chart. */}
      <div className="flex min-w-0 items-center gap-1.5">
        <p className="w-0 max-w-max flex-auto truncate text-[0.8125rem] leading-5 font-medium text-gray-500">
          {criterionName}
        </p>
        {criterionInfoControl}
      </div>

      {note ? (
        <ManagerNoteSurface note={note} metadata="Applies next week" />
      ) : null}

      <p className="text-xs leading-5 text-muted-foreground">
        Alpharun will set the score target and write coaching notes once the
        week begins.
      </p>

      {onEditNote ? (
        <Button
          variant={ButtonVariant.Secondary}
          size={Size.SMALL}
          icon={<IconPencil className="size-4" />}
          onClick={onEditNote}
        >
          {note ? "Edit note" : "Add a note"}
        </Button>
      ) : null}
    </div>
  );
};

export interface NextWeekGoalEditorProps {
  teammateFirstName: string;
  criterionOptions: readonly SimpleTreeSelectOption<string>[];
  /** Section ids, which group the criterion options but can't be picked. */
  disabledCriterionValues: readonly string[];
  /** The saved goal being edited, if any. */
  initialCriterionId?: string;
  initialNote?: string;
  /** "Let Alpharun choose instead" — only offered when editing a saved goal. */
  onUseAutomaticGoal?: () => void;
  onCancel: () => void;
  onSave: (criterionId: string, note: string) => void;
  isSaving?: boolean;
  /** Reports whether the draft differs from the saved goal. */
  onDirtyChange?: (isDirty: boolean) => void;
}

/**
 * The next-week goal editor: a required criterion picker and an optional
 * rep-facing note. Owns its draft state, so a fresh mount resets the form.
 */
export const NextWeekGoalEditor: FC<NextWeekGoalEditorProps> = (props) => {
  const {
    teammateFirstName,
    criterionOptions,
    disabledCriterionValues,
    initialCriterionId,
    initialNote = "",
    onUseAutomaticGoal,
    onCancel,
    onSave,
    isSaving,
    onDirtyChange,
  } = props;

  const [criterionId, setCriterionId] = useState<string | null>(
    initialCriterionId ?? null,
  );
  const [note, setNote] = useState(initialNote);

  const isDirty =
    criterionId !== (initialCriterionId ?? null) || note !== initialNote;
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  return (
    <div className="flex flex-auto flex-col gap-4">
      <div className="max-w-md">
        <SimpleTreeSelect
          label="Criteria focus"
          placeholder="Choose a criteria"
          options={criterionOptions}
          disabledValues={disabledCriterionValues}
          value={criterionId}
          onChange={(value) => setCriterionId(value)}
        />
      </div>

      <div className="flex-auto">
        <Textarea
          label="Note (optional)"
          placeholder={`Leave a note for ${teammateFirstName}...`}
          value={note}
          onChange={(newValue) =>
            setNote(newValue.slice(0, COACHING_MANAGER_NOTE_MAX_LENGTH))
          }
          maxLength={COACHING_MANAGER_NOTE_MAX_LENGTH}
          textareaClassName="min-h-24"
        />
        <div className="mt-1.5 flex items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">
            {teammateFirstName} sees this next week. Alpharun uses it to inform
            that week’s coaching.
          </p>
          {note.length >= NOTE_COUNTER_THRESHOLD ? (
            <p className="text-xs text-muted-foreground tabular-nums">
              {note.length}/{COACHING_MANAGER_NOTE_MAX_LENGTH}
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        {onUseAutomaticGoal ? (
          <Button
            variant={ButtonVariant.Secondary}
            size={Size.MEDIUM}
            onClick={onUseAutomaticGoal}
          >
            Let Alpharun choose instead
          </Button>
        ) : null}

        <div className="ml-auto flex justify-end gap-2">
          <Button
            variant={ButtonVariant.Secondary}
            size={Size.MEDIUM}
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.Primary}
            size={Size.MEDIUM}
            isDisabled={!criterionId}
            isLoading={isSaving}
            onClick={() => {
              if (criterionId) {
                onSave(criterionId, note.trim());
              }
            }}
          >
            Save goal
          </Button>
        </div>
      </div>
    </div>
  );
};
